import {
    ArrowRight,
    ShieldCheck,
    Clock,
    Sparkles,
    BadgeCheck,
    ScanLine,
    ImageUp,
} from "lucide-react";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/bb655671-9efd-41ad-be79-c7624682c623/images/f00eb65f9af7821358d0095a01388c7db3e7c08c25cc339b2589f61a26950e74.png";

export default function Hero() {
    const goQuote = () => {
        document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" });
    };

    const handlePick = (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting the same file later
        if (!file) return;
        window.dispatchEvent(new CustomEvent("pp:photo", { detail: { file } }));
    };

    return (
        <section
            data-testid="hero-section"
            className="relative overflow-hidden bg-carbon pt-28 md:pt-36 pb-20 md:pb-28"
        >
            {/* Cyber grid + glowing blur pods */}
            <div className="absolute inset-0 ai-grid pointer-events-none" aria-hidden />
            <div className="absolute inset-0 pointer-events-none" aria-hidden>
                <div className="absolute -top-28 -left-20 w-[34rem] h-[34rem] bg-safety/15 rounded-full blur-[130px]" />
                <div className="absolute top-10 right-0 w-[32rem] h-[32rem] bg-electric/10 rounded-full blur-[130px]" />
                <div className="absolute bottom-0 left-1/3 w-[26rem] h-[26rem] bg-navy/40 rounded-full blur-[120px]" />
            </div>

            <div className="relative w-full max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                    {/* Left — copy */}
                    <div className="lg:col-span-6 fade-up">
                        <span
                            data-testid="hero-overline"
                            className="inline-flex items-center gap-2 glass text-electric text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full"
                        >
                            <span className="w-2 h-2 rounded-full bg-electric pulse-dot" />
                            Claude Vision AI · Instant Estimate Engine
                        </span>

                        <h1
                            data-testid="hero-headline"
                            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6 tracking-tight"
                        >
                            <span className="gradient-text">
                                Dents &amp; scratches,
                            </span>
                            <br />
                            <span className="text-safety">priced by AI</span>
                            <span className="text-white"> in seconds.</span>
                        </h1>

                        <p
                            data-testid="hero-subtext"
                            className="mt-6 text-lg text-slate-400 max-w-xl leading-relaxed"
                        >
                            Snap a photo and our AI reads the damage instantly —
                            then we match you with a{" "}
                            <span className="font-semibold text-white">
                                trusted local repairer
                            </span>{" "}
                            who confirms your exact price. It&rsquo;s{" "}
                            <span className="font-semibold text-safety">
                                free with no obligation
                            </span>
                            .
                        </p>

                        <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5">
                            <label
                                data-testid="hero-cta-photo"
                                className="group cursor-pointer px-7 py-4 text-sm font-bold tracking-wide uppercase text-white bg-safety hover:bg-safety-dark transition-all duration-200 hover:-translate-y-1 active:scale-95 shadow-glow-orange rounded-2xl inline-flex items-center justify-center gap-2"
                            >
                                <ScanLine size={17} />
                                Snap &amp; Estimate
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    capture="environment"
                                    className="hidden"
                                    data-testid="hero-photo-camera-input"
                                    onChange={handlePick}
                                />
                            </label>
                            <label
                                data-testid="hero-cta-upload"
                                className="group cursor-pointer px-7 py-4 text-sm font-bold tracking-wide uppercase text-white bg-white/5 border border-white/15 hover:border-electric/60 hover:bg-white/10 transition-all active:scale-95 rounded-2xl inline-flex items-center justify-center gap-2"
                            >
                                <ImageUp size={17} className="text-electric" />
                                Upload a Photo
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    className="hidden"
                                    data-testid="hero-photo-upload-input"
                                    onChange={handlePick}
                                />
                            </label>
                            <button
                                onClick={goQuote}
                                data-testid="hero-cta-form"
                                className="group px-4 py-4 text-sm font-bold tracking-wide uppercase text-slate-300 hover:text-electric transition-colors inline-flex items-center justify-center gap-2"
                            >
                                Full Quote
                                <ArrowRight
                                    size={16}
                                    className="group-hover:translate-x-0.5 transition-transform"
                                />
                            </button>
                        </div>

                        <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-medium text-slate-400">
                            <span className="inline-flex items-center gap-2">
                                <ShieldCheck size={18} className="text-electric" />
                                Insurance approved
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <Clock size={18} className="text-electric" />
                                24hr response on most jobs
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <BadgeCheck size={18} className="text-safety" />
                                Free, no-obligation quotes
                            </span>
                        </div>
                    </div>

                    {/* Right — scanning preview card */}
                    <div className="lg:col-span-6 fade-up">
                        <div className="relative">
                            <div className="scanner relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] ring-1 ring-electric/10">
                                <img
                                    src={HERO_IMG}
                                    alt="Panel Pros workshop"
                                    className="w-full h-[300px] md:h-[440px] object-cover"
                                />
                                {/* AI bounding-box overlay mock */}
                                <div className="absolute inset-0 bg-gradient-to-t from-carbon/80 via-transparent to-transparent" />
                                <div className="absolute left-[18%] top-[42%] w-28 h-20 border-2 border-electric/80 rounded-md shadow-glow-cyan">
                                    <span className="absolute -top-6 left-0 text-[10px] font-bold uppercase tracking-wider text-electric bg-carbon/80 px-1.5 py-0.5 rounded">
                                        Dent · 94%
                                    </span>
                                </div>
                            </div>

                            {/* Floating price pill */}
                            <div className="absolute -bottom-5 -left-3 sm:left-6 glass rounded-2xl shadow-glow-orange px-5 py-3.5 animate-float">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                    Instant estimate
                                </div>
                                <div className="font-display font-bold text-2xl leading-none mt-1 text-safety">
                                    NZ$320 – $720
                                </div>
                            </div>

                            {/* Floating AI chip */}
                            <div className="absolute -top-4 right-3 sm:right-6 glass rounded-2xl shadow-glow-cyan px-4 py-3 flex items-center gap-2.5 animate-float-slow">
                                <span className="w-9 h-9 rounded-xl bg-electric/15 grid place-items-center">
                                    <Sparkles size={18} className="text-electric" />
                                </span>
                                <div>
                                    <div className="text-xs font-bold text-white">
                                        AI photo scan
                                    </div>
                                    <div className="text-[11px] text-slate-400">
                                        Result in ~15 sec
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
